import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface AcceptedStatesConfigProps {
  selectedStates: string[];
  onChange: (states: string[]) => void;
  disabled?: boolean;
}

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

const US_STATES = Object.keys(STATE_NAMES);

const REGIONS: Record<string, string[]> = {
  West: ['CA', 'OR', 'WA', 'NV', 'AZ', 'UT', 'CO', 'NM', 'HI', 'AK'],
  Midwest: [
    'IL',
    'IN',
    'IA',
    'KS',
    'MI',
    'MN',
    'MO',
    'NE',
    'ND',
    'OH',
    'SD',
    'WI',
  ],
  South: [
    'AL',
    'AR',
    'FL',
    'GA',
    'KY',
    'LA',
    'MS',
    'NC',
    'OK',
    'SC',
    'TN',
    'TX',
    'VA',
    'WV',
  ],
  Northeast: [
    'CT',
    'DE',
    'DC',
    'ME',
    'MD',
    'MA',
    'NH',
    'NJ',
    'NY',
    'PA',
    'RI',
    'VT',
  ],
  Mountain: ['ID', 'MT', 'WY'],
};

export const AcceptedStatesConfig = ({
  selectedStates,
  onChange,
  disabled,
}: AcceptedStatesConfigProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredStates = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return US_STATES.filter(
      (code) =>
        code.toLowerCase().includes(q) ||
        STATE_NAMES[code].toLowerCase().includes(q),
    );
  }, [search]);

  const toggleState = (code: string) => {
    onChange(
      selectedStates.includes(code)
        ? selectedStates.filter((s) => s !== code)
        : [...selectedStates, code].sort(),
    );
  };

  const toggleRegion = (region: string) => {
    const regionStates = REGIONS[region];
    const allSelected = regionStates.every((s) => selectedStates.includes(s));
    onChange(
      allSelected
        ? selectedStates.filter((s) => !regionStates.includes(s))
        : [...new Set([...selectedStates, ...regionStates])].sort(),
    );
  };

  const removeState = (code: string) => {
    onChange(selectedStates.filter((s) => s !== code));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Accepted States</span>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={disabled}
              className="h-7 text-xs"
            >
              {selectedStates.length === 0
                ? 'All States'
                : `${selectedStates.length} of 51`}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3" align="end">
            {/* Search */}
            <div className="relative mb-3">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search states..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-8 text-sm"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Quick actions */}
            <div className="mb-3 flex gap-1.5">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onChange(US_STATES)}
                className="h-6 flex-1 text-xs"
              >
                All
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onChange([])}
                className="h-6 flex-1 text-xs"
              >
                None
              </Button>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {/* Search results */}
              {filteredStates ? (
                <div className="space-y-1">
                  {filteredStates.length === 0 ? (
                    <p className="py-2 text-center text-xs text-gray-500">
                      No states found
                    </p>
                  ) : (
                    filteredStates.map((code) => (
                      <button
                        type="button"
                        key={code}
                        onClick={() => toggleState(code)}
                        className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm ${
                          selectedStates.includes(code)
                            ? 'bg-blue-50 text-blue-700'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <span>{STATE_NAMES[code]}</span>
                        <span className="text-xs text-gray-400">{code}</span>
                      </button>
                    ))
                  )}
                </div>
              ) : (
                /* Regions view */
                <div className="space-y-3">
                  {Object.entries(REGIONS).map(([region, states]) => {
                    const selected = states.filter((s) =>
                      selectedStates.includes(s),
                    ).length;
                    const allSelected = selected === states.length;
                    return (
                      <div key={region}>
                        <button
                          type="button"
                          onClick={() => toggleRegion(region)}
                          className="mb-1 flex w-full items-center justify-between text-xs font-medium text-gray-600 hover:text-gray-900"
                        >
                          <span>{region}</span>
                          <span className={allSelected ? 'text-blue-600' : ''}>
                            {selected}/{states.length}
                          </span>
                        </button>
                        <div className="flex flex-wrap gap-1">
                          {states.map((code) => (
                            <button
                              type="button"
                              key={code}
                              onClick={() => toggleState(code)}
                              className={`rounded px-1.5 py-0.5 text-xs transition-colors ${
                                selectedStates.includes(code)
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {code}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Selected states preview */}
      {selectedStates.length > 0 && selectedStates.length < 51 && (
        <div className="flex flex-wrap gap-1">
          {selectedStates.slice(0, 10).map((code) => (
            <Badge
              key={code}
              variant="secondary"
              className="h-5 gap-0.5 px-1.5 text-xs font-normal"
            >
              {code}
              <button
                type="button"
                onClick={() => removeState(code)}
                className="ml-0.5 hover:text-red-600"
                disabled={disabled}
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {selectedStates.length > 10 && (
            <Badge variant="outline" className="h-5 px-1.5 text-xs font-normal">
              +{selectedStates.length - 10}
            </Badge>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {selectedStates.length === 0 && 'All US states accepted'}
        {selectedStates.length === 51 && 'All US states selected'}
        {selectedStates.length > 0 &&
          selectedStates.length < 51 &&
          `${51 - selectedStates.length} states excluded`}
      </p>
    </div>
  );
};
